import { BrandMealPeriod } from '../lib/mealPeriods';
import { GROUP_PARTY_FIELD_NAMES } from './groupParty';
import { Mcm } from './mcm';
import { CommonTextTranslation } from './translation';

export interface BrandSegment {
  _id: string;
  name: string;
  color: string;
}

export interface BrandLevel {
  _id: string;
  name: string;
}

export interface BrandAttribute {
  _id: string;
  name: string;
  iconUrl?: string;
}

export interface BrandContentSegment {
  _id: string;
  name: string;
  color: string;
}

export interface BrandContentLevel {
  _id: string;
  name: string;
}

export interface BrandContentAttribute {
  _id: string;
  name: string;
  iconUrl?: string;
}

export interface BrandVenueType {
  _id: string;
  name: string;
  hideFromSignage?: boolean; // Whether we want to show this category in external systems
  mcm?: Mcm;
}

export type HourFormat = '12' | '24';

export interface BrandStaffDepartment {
  _id: string;
  name: string;
}

export interface BrandFontSize {
  min: number;
  max: number;
}

export interface BrandGroupResourceType {
  _id: string;
  name: string;
}

export interface Brand {
  _id: string;
  name: string;
  logo: string;
  active: boolean;
  segments: BrandSegment[];
  levels: BrandLevel[];
  attributes: BrandAttribute[];
  contentSegments: BrandContentSegment[];
  contentLevels: BrandContentLevel[];
  contentAttributes: BrandContentAttribute[];
  venueTypes: BrandVenueType[];
  hourFormat: HourFormat;
  staffDepartments: BrandStaffDepartment[];
  settings?: BrandSettings;
  groupTypes: BrandGroupType[];
  mealPeriods: BrandMealPeriod[];
  dayThemes: BrandDayTheme[];
  timeGroupings: BrandTimeGrouping[];
  imageTypes: BrandImageType[];
  eventHighlights: BrandEventHighlight[];
  commonTexts: BrandCommonText[];
  fontSize: BrandFontSize;
  dateFormat: BrandDateFormat;
  groupResourceTypes: BrandGroupResourceType[];
  formConfig: BrandFormFieldConfig[];
}

export interface BrandSettings {
  notes?: BrandNote[];
}

export interface BrandNote {
  _id: string;
  label: string;
  value: string;
}

export type BrandGroupType = {
  _id: string;
  name: string;
};

export type BrandDayTheme = {
  _id: string;
  name: string;
};

export type BrandTimeGrouping = {
  _id: string;
  name: string;
};

export type BrandImageType = {
  _id: string;
  name: string;
  slug?: string;
};

export type BrandEventHighlight = {
  _id: string;
  name: string;
};

export const BRAND_COMMON_TEXT_CATEGORIES = ['venue', 'display_options', 'groups_label'] as const;

export type BrandCommonTextCategory = typeof BRAND_COMMON_TEXT_CATEGORIES[number];

export const BRAND_COMMON_TEXT_LABELS = [
  'venue_closed',
  'ends_late',
  '24_7',
  'override_start_time',
  'deck',
  'tour_leader',
  'tour_leader_cabin',
  'guest_experience',
  'venue_location_fwd',
  'venue_location_mid',
  'venue_location_aft',
  'venue_location_prt',
  'venue_location_stb',
  'group_contact_name',
  'guests',
  'embark_date',
  'debark_date',
  'group_booking',
  'hospitality_desk',
  'agency_contact_name',
  'sales_dept_email',
  'sales_dept_phone',
  'party_name',
  'group_id',
  'group_type',
  'cabin_drops',
  'dining_preference',
  'prepaid_packages',
  'sales_office',
  'booking_no',
  'group_notes',
  'group_files',
] as const;

export type BrandCommonTextLabel = typeof BRAND_COMMON_TEXT_LABELS[number];

export type BrandCommonText = {
  _id: string;
  name: string;
  label: BrandCommonTextLabel;
  category: BrandCommonTextCategory;
};

export type BrandCommonTextValues = Omit<Record<BrandCommonTextLabel, string>, '24_7'> & {
  twenty_four_seven: string; // Maps to '24_7' label
};

export type CommonTextsLanguageOptions = {
  selected?: string;
};

export type CommonTexts = {
  default: BrandCommonText[];
  translations: CommonTextTranslation[];
};

export type BrandDateFormat = Pick<
  Intl.DateTimeFormatOptions,
  'weekday' | 'month' | 'year' | 'day'
> & {
  useOrdinals?: boolean;
};

export const BRAND_FORM_PREFERENCES_NAME = ['group_party'] as const;

export type BrandFormName = typeof BRAND_FORM_PREFERENCES_NAME[number];

export const BRAND_FORM_FIELD_NAMES = [...GROUP_PARTY_FIELD_NAMES] as const;

export type BrandFormFieldName = typeof BRAND_FORM_FIELD_NAMES[number];

export interface BrandFormFieldPreference {
  fieldName: BrandFormFieldName;
  mandatory: boolean;
}

export interface BrandFormFieldConfig {
  formName: BrandFormName;
  fields: BrandFormFieldPreference[];
}
